import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Tag, Space, Typography, Alert, Table, Modal, Form, InputNumber, Input,
  App as AntApp,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, CopyOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text } = Typography;

interface CodigoConvite {
  codigo: string;
  descricao: string | null;
  criado_por_email: string;
  criado_em: string;
  valido_ate: string;
  usado_em: string | null;
  usado_por_email: string | null;
  revogado_em: string | null;
  revogado_por: string | null;
  status: 'valido' | 'usado' | 'expirado' | 'revogado';
}

const STATUS_TAG: Record<string, { color: string; txt: string }> = {
  valido:   { color: 'success', txt: '✓ Válido' },
  usado:    { color: 'default', txt: 'Usado' },
  expirado: { color: 'warning', txt: 'Expirado' },
  revogado: { color: 'error',   txt: 'Revogado' },
};

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

function AdminCodigosConviteInner() {
  const { message } = AntApp.useApp();
  const [codigos, setCodigos] = useState<CodigoConvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [modalNovo, setModalNovo] = useState(false);
  const [form] = Form.useForm();
  const [gerando, setGerando] = useState(false);
  const [codigoGerado, setCodigoGerado] = useState<CodigoConvite | null>(null);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    setErro(null);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/mobile/admin/codigos-convite`, {
        headers: { Authorization: token },
      });
      const raw: any = r.data;
      const data: any = raw?.body ? JSON.parse(raw.body) : raw;
      setCodigos(data?.codigos || []);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function gerar() {
    const vals = await form.validateFields();
    setGerando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/mobile/admin/codigos-convite`,
        { descricao: vals.descricao || null, horas_validade: vals.horas_validade || 48 },
        { headers: { Authorization: token } },
      );
      const raw: any = r.data;
      const data: any = raw?.body ? JSON.parse(raw.body) : raw;
      setCodigoGerado({
        codigo: data.codigo,
        descricao: data.descricao,
        criado_por_email: '',
        criado_em: new Date().toISOString(),
        valido_ate: data.valido_ate,
        usado_em: null, usado_por_email: null,
        revogado_em: null, revogado_por: null,
        status: 'valido',
      });
      form.resetFields();
      setModalNovo(false);
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally {
      setGerando(false);
    }
  }

  async function revogar(codigo: string) {
    Modal.confirm({
      title: `Revogar código ${codigo}?`,
      content: 'Quem tentar usar vai receber erro.',
      okText: 'Revogar',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/mobile/admin/codigos-convite/${codigo}`, {
            headers: { Authorization: token },
          });
          message.success('Código revogado');
          carregar();
        } catch (e: any) {
          message.error('Falha: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  function compartilharWhatsApp(c: CodigoConvite) {
    const texto = `🔑 Seu código de convite pra Hydrodata Campo:\n\n*${c.codigo}*\n\n` +
      `Válido até ${new Date(c.valido_ate).toLocaleString('pt-BR')}.\n\n` +
      `Acesse: https://campo.hydrodataeng.com.br/welcome`;
    const url = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank');
  }

  function copiarCodigo(codigo: string) {
    navigator.clipboard.writeText(codigo);
    message.success(`${codigo} copiado`);
  }

  const columns = [
    {
      title: 'Código', dataIndex: 'codigo', width: 110,
      render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{v}</Text>,
    },
    { title: 'Descrição', dataIndex: 'descricao', render: (v: string) => v || <Text type="secondary">—</Text> },
    {
      title: 'Criado em', dataIndex: 'criado_em', width: 140,
      render: (v: string) => new Date(v).toLocaleString('pt-BR'),
    },
    {
      title: 'Válido até', dataIndex: 'valido_ate', width: 140,
      render: (v: string) => new Date(v).toLocaleString('pt-BR'),
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: (s: string) => {
        const t = STATUS_TAG[s] || { color: 'default', txt: s };
        return <Tag color={t.color}>{t.txt}</Tag>;
      },
    },
    {
      title: 'Usado por', dataIndex: 'usado_por_email', width: 200,
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Ações', key: 'acoes', width: 220,
      render: (_: any, c: CodigoConvite) => (
        <Space size="small">
          <Button size="small" icon={<CopyOutlined />} onClick={() => copiarCodigo(c.codigo)} disabled={c.status !== 'valido'}>
            Copiar
          </Button>
          <Button size="small" type="primary" ghost onClick={() => compartilharWhatsApp(c)} disabled={c.status !== 'valido'}>
            WhatsApp
          </Button>
          <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => revogar(c.codigo)} disabled={c.status !== 'valido'}>
            Revogar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ margin: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>🔑 Códigos de Convite — Campo</Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>Atualizar</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalNovo(true)}>Gerar código</Button>
          </Space>
        </div>

        <Alert
          type="info"
          showIcon
          message="Como funciona"
          description={
            <div>
              <ol style={{ marginBottom: 0, paddingLeft: 18 }}>
                <li>Gera um código (válido por 48h por padrão).</li>
                <li>Manda no WhatsApp da pessoa (botão "WhatsApp" copia mensagem pronta).</li>
                <li>A pessoa entra em <strong>campo.hydrodataeng.com.br/welcome</strong>, digita o código + nome + qualquer email dela.</li>
                <li>Cognito manda senha temporária pro email dela.</li>
                <li>Acesso liberado no grupo <code>campo</code>.</li>
              </ol>
            </div>
          }
        />

        {erro && <Alert type="error" message={erro} />}

        <Table
          rowKey="codigo"
          dataSource={codigos}
          columns={columns as any}
          loading={loading}
          pagination={{ pageSize: 25 }}
          locale={{ emptyText: 'Nenhum código gerado ainda' }}
        />
      </Space>

      <Modal
        open={modalNovo}
        onCancel={() => setModalNovo(false)}
        title="Gerar código de convite"
        onOk={gerar}
        confirmLoading={gerando}
        okText="Gerar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" initialValues={{ horas_validade: 48 }}>
          <Form.Item label="Descrição (opcional)" name="descricao" tooltip="Pra você se lembrar pra quem é. Ex: 'João Silva — CON-2026-014'">
            <Input placeholder="Pra quem é este código?" maxLength={200} />
          </Form.Item>
          <Form.Item label="Validade (horas)" name="horas_validade" tooltip="48h padrão. Máx 720h (30 dias).">
            <InputNumber min={1} max={720} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!codigoGerado}
        onCancel={() => setCodigoGerado(null)}
        title="Código gerado!"
        footer={[
          <Button key="close" onClick={() => setCodigoGerado(null)}>Fechar</Button>,
          <Button key="copy" icon={<CopyOutlined />} onClick={() => codigoGerado && copiarCodigo(codigoGerado.codigo)}>
            Copiar
          </Button>,
          <Button key="wa" type="primary" onClick={() => codigoGerado && compartilharWhatsApp(codigoGerado)}>
            Enviar via WhatsApp
          </Button>,
        ]}
      >
        {codigoGerado && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ textAlign: 'center', padding: 20, background: '#f0f5ff', borderRadius: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Código</Text>
              <div style={{ fontFamily: 'monospace', fontSize: 32, fontWeight: 700, letterSpacing: 4, color: '#0930D0' }}>
                {codigoGerado.codigo}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Válido até {new Date(codigoGerado.valido_ate).toLocaleString('pt-BR')}
              </Text>
            </div>
            <Text>
              Mande este código pra pessoa. Ela usa em <strong>campo.hydrodataeng.com.br/welcome</strong>.
            </Text>
          </Space>
        )}
      </Modal>
    </Card>
  );
}

export default function AdminCodigosConvite() {
  return <AntApp><AdminCodigosConviteInner /></AntApp>;
}
